<script>
  import { onDestroy, onMount } from "svelte";
  import { Label, Input } from "sveltestrap";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import { dTypeIcon, getFieldDataType } from "../../editor/component/data.js";
  import {
    callQueue,
    data,
    dataInfo,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    currMenu,
    currentElementLockGroup,
    directContext,
  } from "../../editor/storage.js";
  import DataColumns from "../component/dataColumns.svelte";
  import { isValidValud } from "../../editor/utility.js";
  import RCFieldEditor from "../component/rcFieldEditor.svelte";
  import ItemResponse from "../component/ItemResponse.svelte";
  import SplitHeaderEditor from "../component/splitHeaderLabelEditor.svelte";
  import { makeRGBAString } from "../../editor/component/mark.js";

  // event functions
  export let inputFocusAction;
  export let inputBlurAction;
  export let openThirdMenu;
  export let closeThirdMenu;

  let layoutType;
  let nColumns;
  let splitField;
  let splitValues;
  let splitLabels;
  let columns;
  let rows;
  let composition_lock, transpose_lock, multiples_lock;

  function setValues() {
    layoutType = $currentSpec?.fronto?.layout?.composition || undefined;
    nColumns = $currentSpec?.fronto?.layout?.nColumns || undefined;
    splitField = $currentSpec?.fronto?.layout?.split?.field || undefined;
    splitValues =
      JSON.stringify($currentSpec?.fronto?.layout?.split?.values || []) || "[]";
    splitValues = splitValues?.slice(1, splitValues.length - 1);
    splitLabels = $currentSpec?.fronto?.layout?.split?.label || undefined;
    columns = $currentSpec?.fronto?.layout?.column || [];
    rows = $currentSpec?.fronto?.layout?.row || [];
    composition_lock = $currentSpec?.fronto?.layout?.lock?.composition;
    transpose_lock = $currentSpec?.fronto?.layout?.lock?.transpose;
    multiples_lock = $currentSpec?.fronto?.layout?.lock?.multiples;
  }

  // updates;
  function updateLayoutType(event) {
    let newType = event.target.value;
    if (["single", "repeated", "projection"].includes(newType)) {
      let rule = {
        category: "manual",
        description: "Set the layout type",
        specifier: { role: "view" },
        action: "modify",
        option: {
          composition: newType,
        },
      };
      if (newType === "projection") {
        rule.option.projection = { type: "mercator" }; // default;
      }
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    } else {
      console.log("Layout type is not valid:" + newType);
    }
  }

  function updateSplitField(newField) {
    // let newField = event.target.value;
    if ($dataInfo.columns.includes(newField)) {
      let rule = {
        category: "manual",
        description: "Set a multiples field",
        specifier: { role: "view" },
        action: "modify",
        option: {
          split: {
            field: newField,
          },
        },
      };
      if (nColumns === undefined) rule.option.split.nColumns = 1;
      if (splitValues === undefined)
        rule.option.split.values = $dataInfo.stats[newField].values;
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    } else if (newField === null) {
      let rule = {
        category: "manual",
        description: "Unset a multiples field",
        specifier: { role: "view" },
        action: "modify",
        option: {
          split: {
            field: null,
          },
        },
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    } else {
      console.log("Split field is not valid:" + newField);
    }
  }

  function updateNColumns(event) {
    let newValue = parseInt(event.target.value);
    if (!isNaN(newValue)) {
      let rule = {
        category: "manual",
        description: "Modify # columns",
        specifier: { role: "view" },
        action: "modify",
        option: {
          nColumns: newValue,
        },
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    } else {
      console.log("# of columns is not valid:" + newValue);
    }
  }

  function updateSplitValues(event) {
    let newValue;
    try {
      newValue = JSON.parse("[" + event.target.value + "]");
    } catch (e) {
      console.warn("Split values are not valid:" + event.target.value);
    }
    if (newValue) {
      let rule = {
        category: "manual",
        description: "Modify multiples values",
        specifier: { role: "view" },
        action: "modify",
        option: {
          split: {
            values: newValue,
          },
        },
      };
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    } else {
      console.log("Layout type is not valid:" + newValue);
    }
  }

  function updateSplitHeader(event, role, label, option) {
    let rule;
    if (role === "lock") {
      let newValue = event.target.checked;
      rule = {
        description: (newValue ? "Lock" : "Unlock") + " multiples header label",
        specifier: {
          role: "facet.label",
        },
        action: "lock",
        option: {
          label: newValue,
        },
      };
    } else if (role === "label.items" && event === "duplicate") {
      let dup;
      if (label.items) dup = deepcopy(label.items[label.items.length - 1]);
      else dup = {};
      rule = {
        category: "manual",
        description: "Add a new line for multiples header",
        specifier: { role: "facet.label" },
        action: "add",
        option: {
          items: dup,
        },
      };
    } else {
      let newValue;
      if (
        role.match(
          /^label\.items\.[0-9]+(\.(|width|align|fontWeight|fontStroke|format|expression|fontSize|opacity|content|color|custom))?$/
        )
      ) {
        let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
        if (itemIndex !== undefined) {
          if (
            role.endsWith(".width") ||
            role.endsWith(".fontSize") ||
            role.endsWith(".opacity")
          ) {
            newValue = parseFloat(event.target.value);
          } else if (
            role.endsWith(".align") ||
            role.endsWith(".fontWeight") ||
            role.endsWith(".format") ||
            role.endsWith(".expression") ||
            role.endsWith(".content")
          ) {
            newValue = event.target.value;
          } else if (role.endsWith(".color") || role.endsWith(".fontStroke")) {
            if (event.target?.value?.startsWith("$")) {
              newValue = event.target.value;
            } else {
              newValue = makeRGBAString(
                event.detail.r,
                event.detail.g,
                event.detail.b,
                event.detail.a
              );
            }
          } else if (role.endsWith(".custom")) {
            newValue = customListToObj(
              event?.constructor.name === "Array"
                ? event
                : label.items?.[itemIndex]?.customList || []
            );
            if (event.newCustomValue !== undefined) {
              newValue[event.newCustomValue] = event.newCustomText;
            }
          } else if (
            event === "remove" &&
            role.match(/^label\.items\.[0-9]+/)
          ) {
            newValue = null;
          }
        }
      } else if (role === "label.offset") {
        newValue = event.target.value;
      }
      if (newValue !== undefined || newValue == null) {
        rule = {
          description: "Edit multiples label ",
          specifier: {
            role: "facet.label",
          },
          action: "modify",
          option: {},
        };
        if (
          role.match(
            /^label\.items\.[0-9]+(\.(|width|align|fontWeight|fontStroke|format|expression|fontSize|opacity|content|color|custom))?$/
          )
        ) {
          let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
          rule.description += `text (${itemIndex}) `;
          if (itemIndex !== undefined) {
            if (
              role.endsWith(".values") ||
              role.endsWith(".width") ||
              role.endsWith(".fontSize") ||
              role.endsWith(".opacity") ||
              role.endsWith(".align") ||
              role.endsWith(".fontWeight") ||
              role.endsWith(".fontStroke") ||
              role.endsWith(".color") ||
              role.endsWith(".fontStroke") ||
              role.endsWith(".format") ||
              role.endsWith(".expression") ||
              role.endsWith(".custom") ||
              role.endsWith(".content")
            ) {
              let optionKey = role.split(".")[3];
              rule.description += " (" + optionKey + ")";
              rule.option = { items: { index: itemIndex } };
              rule.option.items[optionKey] = newValue;
            } else if (
              event === "delete" &&
              role.match(/^label\.items\.[0-9]+/)
            ) {
              rule.description = rule.description.replace("Edit ", "Remove ");
              rule.action = "remove";
              rule.option = { items: { index: itemIndex } };
            }
          }
        } else if (role === "label.offset") {
          rule.option.offset = newValue;
          rule.description += "offset";
        }
      }
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  function updateRCFields(rc, action, option) {
    let rule;
    let rules = [];
    if (
      ["column", "row"].includes(rc) &&
      ["modify", "add", "remove", "replace", "swap"].includes(action)
    ) {
      rule = {
        category: "manual",
        specifier: { role: "view." + rc },
        action,
      };
      if (option !== undefined) rule.option = {};
      if (action === "modify" && option.existing) {
        rule.description = "Modify a " + rc + " field";
        rule.specifier.field = option.existing.field;
        if (option.shelfIndex !== undefined)
          rule.specifier.index = option.shelfIndex;
        if (option.isCount) {
          rule.option.aggregate = "count";
          rule.option.field = null;
        } else if (option.fieldName !== undefined) {
          rule.option.field = option.fieldName;
        }
      } else if (action === "add" && option.fieldName) {
        rule.description = "Add a " + rc + " field";
        if (option.shelfIndex !== undefined)
          rule.option.index = option.shelfIndex;
        if (option.isCount) {
          rule.option.aggregate = "count";
          rule.option.field = null;
        } else if (option.fieldName !== undefined) {
          rule.option.field = option.fieldName;
        }
      } else if (action === "remove" && option.fieldName) {
        rule.description = "Remove a " + rc + " field";
        rule.specifier.field = option.fieldName;
        if (option.shelfIndex !== undefined)
          rule.specifier.index = option.shelfIndex;
      } else if (
        action === "replace" &&
        option.fieldName &&
        option?.to?.shelf
      ) {
        rule.description = "Rplace a " + rc + " field to " + option.to.shelf;
        rule.specifier.field = option.fieldName;
        if (option.existingIndex !== undefined)
          rule.specifier.index = option.existingIndex;
        rule.option.to = { role: "view." + option.to.shelf };
        if (option.to.shelfINdex) rule.option.to.index = option.to.shelfIndex;
      } else if (action === "swap" && !option) {
        rule.description = "Swap " + rc + " order";
      }

      // add/modify--adjustment
      if (action === "modify" || action === "add") {
        if ($dataInfo.columns.includes(option.fieldName)) {
          rule.option.type = $dataInfo.stats[option.fieldName].type;
          if (
            ["nominal", "ordinal"].includes(
              $dataInfo.stats[option.fieldName].type
            )
          ) {
            rule.option.values = $dataInfo.stats[option.fieldName].values;
          }
        }
      }
    } else if (rc === "rc" && action === "swap") {
      rule = {
        category: "manual",
        description: "Transpose axes",
        specifier: { role: "view" },
        action: "transpose",
      };
    }
    if (rule) {
      // todo: test this
      let add_subrule = false;
      if (action === "add" && $currentSpec.fronto?.layer?.length == 0) {
        add_subrule = true;
      } else if (action === "add" && $currentSpec.fronto?.layer?.length == 1) {
        add_subrule = true;
        $currentSpec.fronto?.layer[0].text?.forEach((t) => {
          if (t.field === rule.option?.field && t.type === "on-axis")
            add_subrule = false;
        });
      }
      if (add_subrule) {
        let subrule = {
          category: "manual",
          description: "Add axis labels",
          specifier: { role: "layer", index: 0 },
          action: "add",
          option: {
            role: "text",
            text: {
              type: "axis",
              field: rule.option.field,
            },
          },
        };
        rules.push(subrule);
      }

      if (action === "remove") {
        $currentSpec.fronto?.layer?.forEach((layer) => {
          layer.text?.forEach((t) => {
            if (t.field === rule.specifier.field && t.type === "on-axis") {
              let subrule = {
                category: "manual",
                description: "Remove axis labels",
                specifier: { role: "axis.label", field: t.field },
                action: "remove",
              };
              rules.push(subrule);
            }
          });
        });
      }
      rules.push(rule);
      callQueue.update((queue) => {
        queue.add(rules, $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  // dragging
  let dragged;
  function assignToShelf(event) {
    // drop event
    event.preventDefault();
    if (!event.target.classList.contains("shelf")) return false;
    // shelf: row/column/split
    let fieldName = dragged.getAttribute("data-field");
    let shelf,
      rcArray,
      existingShelf,
      existingIndex,
      isCount,
      onBadge = false;
    let shelfIndex = parseInt(event.target.getAttribute("shelf-index"));
    if (
      ((existingShelf = dragged.getAttribute("badge-shelf")), existingShelf) !==
      undefined
    ) {
      existingIndex = parseInt(dragged.getAttribute("shelf-index"));
    }
    // count
    if (dragged.getAttribute("data-agg") === "count") {
      isCount = true;
    }
    // other cases
    if (event.target.classList.contains("shelf-columns")) {
      shelf = "column";
    } else if (event.target.classList.contains("shelf-rows")) {
      shelf = "row";
    } else if (event.target.classList.contains("shelf-split")) {
      shelf = "split";
    }
    if (shelf === "column") rcArray = columns;
    else if (shelf === "row") rcArray = rows;
    let addAt0 = shelfIndex == 0 && rcArray.length == 1;
    let withinSwap = shelfIndex != existingIndex && existingShelf === shelf;

    // no self assignment
    if (shelfIndex == existingIndex && existingShelf === shelf) return false;
    else if (withinSwap) {
      updateRCFields(existingShelf, "swap");
    } else if (existingShelf) {
      updateRCFields(existingShelf, "replace", {
        fieldName,
        existingIndex,
        to: { shelf, shelfIndex },
      });
    } else if (shelf == "column" || shelf == "row") {
      let filledShelf =
        event.target.getAttribute("data-filled") === "true" || onBadge;
      if (filledShelf && !addAt0) {
        updateRCFields(shelf, "modify", {
          fieldName,
          shelfIndex,
          isCount,
          existing: rcArray[shelfIndex],
        });
      } else {
        if (rcArray.length == 0 || addAt0) shelfIndex = 0;
        if (addAt0) {
          updateRCFields(shelf, "add", { fieldName, shelfIndex, isCount });
        } else if (rcArray.length < 2)
          updateRCFields(shelf, "add", { fieldName, shelfIndex, isCount });
      }
    } else if (shelf == "split") {
      if (isCount) return false;
      updateSplitField(fieldName);
    }
  }
  function detachFromShelf(event) {
    // drop event
    event.preventDefault();
    if (event.target.classList.contains("shelf")) return false;
    if (dragged) {
      let fieldName = dragged.getAttribute("data-field");
      let fieldAgg = dragged.getAttribute("data-agg");
      let shelf = dragged.getAttribute("badge-shelf");
      let shelfIndex = parseInt(dragged.getAttribute("shelf-index"));
      // RC
      if (
        !fieldAgg &&
        [fieldName, shelf, shelfIndex].every((d) => d !== undefined)
      ) {
        if (["row", "column"].includes(shelf))
          updateRCFields(shelf, "remove", { fieldName, shelfIndex });
        else if (shelf === "split") updateSplitField(null);
      } else if (
        fieldAgg === "count" &&
        [shelf, shelfIndex].every((d) => d !== undefined)
      ) {
        if (["row", "column"].includes(shelf))
          updateRCFields(shelf, "remove", { isCount: true, shelfIndex });
      }
    }
  }
  let toBeDetached;
  function shelfDragEnd(event) {
    dragged?.classList.remove("dragged");
    if (toBeDetached) {
      detachFromShelf(event);
    }
    dragged = undefined;
    toBeDetached = false;
    document.body.classList.remove("detached");
    dragged?.classList.remove("detached");
    document.querySelector("#detached-tracker").style.display = "none";
  }
  function beingDetached(event) {
    if (
      dragged &&
      (event.target.classList.contains("shelf") ||
        event.target.classList.contains("field-badge"))
    ) {
      toBeDetached = false;
      document.body.classList.remove("detached");
      dragged?.classList.remove("detached");
      document.querySelector("#detached-tracker").style.display = "none";
    } else if (dragged) {
      toBeDetached = true;
      document.body.classList.add("detached");
      dragged?.classList.add("detached");
      document.querySelector("#detached-tracker").style.display = "block";
      document.querySelector("#detached-tracker").style.left =
        event.clientX + 15 + "px";
      document.querySelector("#detached-tracker").style.top =
        event.clientY + 5 + "px";
    }
  }
  function shelfDragStart(event) {
    if (!event.target.classList.contains("window-drag")) {
      dragged = event.target;
      dragged.classList.add("dragged");
    }
  }

  // swap
  function swapRCShelves() {
    updateRCFields("rc", "swap");
  }

  // editing each channel
  let currField,
    currFieldRole,
    showFieldPanel = false;
  function editField(pos, role) {
    currField = pos;
    currFieldRole = role;
    setEncodingMenu(pos);
    openThirdMenu();
    showFieldPanel = true;
  }
  function finishFieldEdit() {
    currField = undefined;
    currFieldRole = undefined;
    showFieldPanel = false;
    closeThirdMenu();
  }

  let currEncType,
    currEncDataType,
    currEncSort,
    currBin,
    currBinCount,
    currAggregate,
    currEncScaleType,
    currEncFieldType;
  function setEncodingMenu(pos) {
    if (pos?.value !== undefined) {
      currEncType = "static";
    } else if (pos !== undefined) {
      currEncType = "encoding";
      if (pos?.field !== undefined) {
        currEncFieldType = getFieldDataType(pos?.field, $dataInfo);
        currEncDataType = $dataInfo?.stats[pos?.field]?.type;
      } else if (pos?.aggregate === "count") {
        currEncFieldType = "number";
      }
      if (pos?.sort !== undefined) {
        if (pos?.sort?.order !== undefined) {
          currEncSort = "custom";
        } else {
          currEncSort = pos?.sort;
        }
      } else {
        currEncSort = "none";
      }
      currAggregate = pos?.aggregate || undefined;
      if (pos?.bin !== undefined && pos?.bin !== null) {
        currBin = true;
        currBinCount = pos?.bin.maxbins;
      } else {
        currBin = false;
      }
      if (pos?.scale === undefined) {
        currEncScaleType = "default";
      } else if (pos?.scale !== undefined) {
        if (pos?.scale.domain !== undefined || pos?.scale.range !== undefined) {
          currEncScaleType = "domain_range";
        } else if (pos?.scale.scheme !== undefined) {
          currEncScaleType = "scheme_only";
          currScaleScheme = pos?.scale.scheme;
        } else {
          currEncScaleType = "default";
        }
      }
    } else {
      currEncType = undefined;
      currEncSort = "none";
    }
  }
  function updateRCField(event, enc, role, _) {
    let newValue;
    if (enc === "lock") {
      updateLock(event, role);
      return;
    } else if (role === "encoding.type") {
      newValue = event.target.value;
    } else if (role === "encoding.sort") {
      newValue = event.target.value;
      let field =
        $currentSpec.fronto?.layout?.[currFieldRole[0]]?.[currFieldRole[1]];
      if (newValue === "custom") {
        let values = $dataInfo.stats[field?.field].values;
        if (values) newValue = { order: values };
      } else if (newValue === "none") {
        newValue = null;
      }
    } else if (role === "encoding.sort.order") {
      newValue = event.target.value.split(",").map((d) => d.trim());
    } else if (role === "encoding.aggregate") {
      if (event.constructor.name === "String") newValue = event;
      else newValue = event.target.value;
    } else if (role === "encoding.bin") {
      newValue = event.target.checked || null;
    } else if (role === "encoding.bin.nBins") {
      newValue = parseInt(event.target.value);
    } else if (role === "encoding.scale.type") {
      if (event.target.value === "default") newValue = null;
    } else if (role === "encoding.scale.values") {
      newValue = event;
    } else if (role === "encoding.stack") {
      newValue = event.target.value;
    }
    let rule;
    if (newValue || newValue == null) {
      rule = {
        description: `Modify ${currFieldRole.join("/")} encoding `,
        specifier: { role: currFieldRole[0], index: currFieldRole[1] },
        action: "modify",
        option: {},
      };
      // specifier
      if (field.field !== undefined) {
        rule.specifier.field = field.field;
      } else if (field.aggregate !== undefined) {
        rule.specifier.aggregate = field.aggregate;
      }
      // option
      if (role === "encoding.field") {
        rule.description += "field";
        rule.option.field = newValue;
        let newType = $dataInfo.stats[newValue]?.type;
        if (newType) {
          rule.option.type = newType;
        }
      } else if (role === "encoding.type") {
        rule.description += "type";
        rule.option.type = newValue;
      } else if (role === "encoding.sort") {
        rule.description += "sort";
        rule.option.sort = newValue;
      } else if (role === "encoding.sort.order") {
        rule.description += "sort order";
        rule.option.sort = { order: newValue };
      } else if (role === "encoding.aggregate") {
        rule.description += "aggregate";
        rule.option.aggregate = newValue;
      } else if (role === "encoding.bin") {
        if (newValue) {
          rule.description += "bin (set)";
          rule.option.bin = true;
        } else {
          rule.description += "bin (unset)";
          rule.option.bin = null;
        }
      } else if (role === "encoding.bin.nBins") {
        rule.option.bin = { nBins: newValue };
      } else if (role === "encoding.scale.type") {
        rule.description += "scale type";
        if (newValue == null) rule.option.scale = null;
      } else if (role === "encoding.scale.values") {
        let domain_valid = newValue.domain_values?.every(isValidValud);
        let range_valid = newValue.range_values?.every(isValidValud);
        if (domain_valid && range_valid) {
          rule.description += "scale domain and range";
          rule.option.scale = {
            domain: newValue.domain_values,
            range: newValue.range_values,
          };
        } else if (domain_valid) {
          rule.description += "scale domain";
          rule.option.scale = {
            domain: newValue.domain_values,
          };
        } else if (range_valid) {
          rule.description += "scale range";
          rule.option.scale = {
            range: newValue.range_values,
          };
        } else {
          rule = undefined;
        }
      } else if (role === "encoding.stack") {
        rule.description += "stack type";
        rule.option.stack = newValue;
      }
    }
    if (rule) {
      console.log(rule);
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  function updateLock(event, role) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role) {
        let rule;
        if (role === "composition") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " layout type",
            specifier: {
              role: "view",
            },
            action: "lock",
            option: {
              composition: newValue,
            },
          };
        } else if (role === "transpose") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " transpose",
            specifier: {
              role: "view",
            },
            action: "lock",
            option: {
              transpose: newValue,
            },
          };
        } else if (role.startsWith("row/") || role.startsWith("column/")) {
          rule = {
            category: "lock",
            description:
              (newValue ? "Lock" : "Unlock") +
              ` ${currFieldRole.join("/")} channel`,
            specifier: { role: currFieldRole[0], index: currFieldRole[1] },
            action: "lock",
            option: { encoding: newValue },
          };
        } else if (role === "multiples.header") {
          let newValue = event.target.checked;
          rule = {
            description:
              (newValue ? "Lock" : "Unlock") + " multiples header label",
            specifier: {
              role: "facet.label",
            },
            action: "lock",
            option: {
              label: newValue,
            },
          };
        } else if (role === "multiples") {
          rule = {
            category: "lock",
            description: (newValue ? "Lock" : "Unlock") + " multiples layout",
            specifier: {
              role: "view",
            },
            action: "lock",
            option: {
              nColumns: newValue,
            },
          };
        }

        if (rule) {
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }

  function updatePadding(event) {
    let dir = event.target.name.split("-")[1];
    let newValue = parseFloat(event.target.value);
    if (!isNaN(newValue)) {
      let rule = {
        category: "manual",
        description: `Modify the view padding (${dir})`,
        specifier: { role: "view" },
        action: "modify",
        option: {
          padding: {
            [dir]: newValue,
          },
        },
      };
      if (rule) {
        callQueue.update((queue) => {
          queue.add([rule], $currentElementLockGroup).apply();
          return queue;
        });
      }
    }
  }

  // pipelines
  onMount(() => {
    setValues();
    document.addEventListener("dragover", beingDetached, false);
    document.addEventListener("dragend", shelfDragEnd, false);
    currField = undefined;
    currFieldRole = undefined;
    showFieldPanel = false;
  });
  currentSpec.subscribe(() => {
    setValues();
    if (currFieldRole && currFieldRole.length == 2) {
      let currFieldCand =
        $currentSpec.fronto?.layout?.[currFieldRole[0]]?.[currFieldRole[1]];
      if (currFieldCand) {
        currField = currFieldCand;
        setEncodingMenu(currField);
      }
    }
  });
  onDestroy(() => {
    document.removeEventListener("dragover", beingDetached, false);
    document.removeEventListener("dragend", shelfDragEnd, false);
    currField = undefined;
    currFieldRole = undefined;
  });
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["layout"]?.title || "Layout"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !$data}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Set data first!</p>
      </div>
    </div>
  {:else}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Edit the layout of an artboard.</p>
      </div>
    </div>
    <!-- Layout type: single; small multiples; projection -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="layout_type">Layout type</Label>
      </div>
      <div class="dupo-menu-pane-form-row">
        <Input
          name="layout_type"
          type="select"
          class="custom-form-select"
          on:focus={inputFocusAction}
          on:blur={inputBlurAction}
          on:change={(event) => {
            updateLayoutType(event);
          }}
          value={$currentSpec?.fronto?.layout?.composition}
        >
          <option value={undefined}>Select</option>
          <option value="single">Single</option>
          <option value="repeated">Small multiples</option>
          <option value="projection">Projection (map)</option>
        </Input>
      </div>
    </div>
    {#if $currentSpec?.fronto?.layout?.composition === "single"}
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          type="boxPadding"
          title="View Padding"
          token={"view-padding"}
          value={$currentSpec?.fronto?.layout.padding || {}}
          {inputFocusAction}
          {inputBlurAction}
          changeAction={(event) => {
            updatePadding(event);
          }}
        />
      </div>
    {/if}

    <!-- lock -->
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        type="lock"
        tooltipMessage="Prevent suggesting responsive alternatives that make changes to the layout type. Element-Lock applies to its source or target design(s)."
        title="Lock layout type"
        token={"composition-lock"}
        value={composition_lock}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateLock(event, "composition");
        }}
      />
    </div>
    <!-- Column -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="columns">Columns</Label>
      </div>
      <div class="dupo-menu-pane-row shelf-wrap">
        <div
          class="shelf shelf-columns"
          data-filled={columns?.[0] ? "true" : "false"}
          shelf-index="0"
          ondragover="return false"
          on:drop={assignToShelf}
        >
          {#if columns?.[0]?.field}
            <span
              data-field={columns[0].field}
              class={"field-badge " + $dataInfo.stats[columns[0].field]?.type}
              draggable="true"
              badge-shelf="column"
              shelf-index="0"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(columns[0], ["column", 0])}
            >
              <i
                class={"bi bi-" +
                  dTypeIcon[$dataInfo.stats[columns[0].field]?.type] +
                  " me-1"}
              />
              {columns[0].field}
            </span>
          {:else if columns?.[0]?.aggregate === "count"}
            <span
              data-field="count"
              data-agg="count"
              class={"field-badge " + "quantitative"}
              draggable="true"
              badge-shelf="column"
              shelf-index="0"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(columns[0], ["column", 0])}
            >
              <i class={"bi bi-" + dTypeIcon["count"] + " me-1"} />
              Count
            </span>
          {/if}
        </div>
        <div
          class="shelf shelf-columns"
          ondragover="return false"
          data-filled={columns?.[1] ? "true" : "false"}
          shelf-index="1"
          on:drop={assignToShelf}
        >
          {#if columns?.[1]?.field}
            <span
              data-field={columns[1].field}
              class={"field-badge " + $dataInfo.stats[columns[1].field]?.type}
              draggable="true"
              badge-shelf="column"
              shelf-index="1"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(columns[1], ["column", 1])}
            >
              <i
                class={"bi bi-" +
                  dTypeIcon[$dataInfo.stats[columns[1].field]?.type] +
                  " me-1"}
              />
              {columns[1].field}
            </span>
          {:else if columns?.[1]?.aggregate === "count"}
            <span
              data-field="count"
              data-agg="count"
              class={"field-badge " + "quantitative"}
              draggable="true"
              badge-shelf="column"
              shelf-index="1"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(columns[1], ["column", 1])}
            >
              <i class={"bi bi-" + dTypeIcon["count"] + " me-1"} />
              Count
            </span>
          {/if}
        </div>
      </div>
    </div>
    <!-- Row -->
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <Label for="rows">Rows</Label>
      </div>

      <div class="dupo-menu-pane-row shelf-wrap">
        <div
          class="shelf shelf-rows"
          data-filled={rows?.[0] ? "true" : "false"}
          shelf-index="0"
          ondragover="return false"
          on:drop={assignToShelf}
        >
          {#if rows?.[0]?.field}
            <span
              data-field={rows[0].field}
              class={"field-badge " + $dataInfo.stats[rows[0].field]?.type ||
                "count"}
              draggable="true"
              badge-shelf="row"
              shelf-index="0"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(rows[0], ["row", 0])}
            >
              <!-- todo: drag move feedback -->
              <i
                class={"bi bi-" +
                  dTypeIcon[$dataInfo.stats[rows[0].field]?.type || "count"] +
                  " me-1"}
              />
              {rows[0].field}
            </span>
          {:else if rows?.[0]?.aggregate === "count"}
            <span
              data-field="count"
              data-agg="count"
              class={"field-badge " + "quantitative"}
              draggable="true"
              badge-shelf="row"
              shelf-index="0"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(rows[0], ["row", 0])}
            >
              <i class={"bi bi-" + dTypeIcon["count"] + " me-1"} />
              Count
            </span>
          {/if}
        </div>
        <div
          class="shelf shelf-rows"
          data-filled={rows?.[1] ? "true" : "false"}
          shelf-index="0"
          ondragover="return false"
          on:drop={assignToShelf}
        >
          {#if rows?.[1]?.field}
            <span
              data-field={rows[1].field}
              class={"field-badge " + $dataInfo.stats[rows[1].field].type}
              draggable="true"
              badge-shelf="row"
              shelf-index="1"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(rows[1], ["row", 1])}
            >
              <!-- todo: drag move feedback -->
              <i
                class={"bi bi-" +
                  dTypeIcon[$dataInfo.stats[rows[1].field].type] +
                  " me-1"}
              />
              {rows[1].field}
            </span>
          {:else if rows?.[1]?.aggregate === "count"}
            <span
              data-field="count"
              data-agg="count"
              class={"field-badge " + "quantitative"}
              draggable="true"
              badge-shelf="row"
              shelf-index="1"
              ondragover="return false"
              on:drop={assignToShelf}
              on:dragstart={(event) => {
                shelfDragStart(event);
              }}
              on:dblclick={() => editField(rows[1], ["row", 1])}
            >
              <i class={"bi bi-" + dTypeIcon["count"] + " me-1"} />
              Count
            </span>
          {/if}
        </div>
      </div>
      <div class="dupo-menu-pane-row mt-2">
        <p class="text-sm">
          The last column/row field is the <em>x/y</em> encoding.
        </p>
        <p class="text-sm">Double-click a field to edit the scale.</p>
      </div>
    </div>
    <div class="pane-divider" />
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-form-row">
        <button
          type="button"
          class="menu-second-level"
          on:click={(event) => swapRCShelves()}
          ><i class="bi bi-arrow-left-right" /> Swap rows and columns</button
        >
      </div>
    </div>

    <!-- lock -->
    <div class="dupo-menu-pane mb-2">
      <ItemResponse
        type="lock"
        tooltipMessage="Prevent suggesting responsive alternatives that transpose the chart. Element-Lock applies to its source or target design(s)."
        title="Lock axis-transpose"
        token={"transpose-lock"}
        value={transpose_lock}
        {inputFocusAction}
        {inputBlurAction}
        changeAction={(event) => {
          updateLock(event, "transpose");
        }}
      />
    </div>

    <!-- Split -->
    {#if layoutType == "repeated"}
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="split_field">Field to repeat</Label>
        </div>

        <div class="dupo-menu-pane-row shelf-wrap">
          <div
            class="shelf shelf-split"
            data-filled={splitField ? "true" : "false"}
            ondragover="return false"
            on:drop={assignToShelf}
          >
            {#if splitField}
              <span
                data-field={splitField}
                class={"field-badge " + $dataInfo.stats[splitField].type}
                draggable="true"
                badge-shelf="split"
                shelf-index="0"
                ondragover="return false"
                on:drop={assignToShelf}
                on:dragstart={(event) => {
                  shelfDragStart(event);
                }}
              >
                <i
                  class={"bi bi-" +
                    dTypeIcon[$dataInfo.stats[splitField].type] +
                    " me-1"}
                />
                {splitField}
              </span>
            {/if}
          </div>
        </div>
      </div>
    {/if}
    {#if layoutType == "repeated" && splitField !== undefined}
      <div class="dupo-menu-pane mb-2">
        <div class="dupo-menu-pane-row">
          <Label for="split_n_columns">Number of Columns</Label>
        </div>
        <div class="dupo-menu-pane-form-row">
          <Input
            name="split_n_columns"
            type="number"
            class="custom-form-select"
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateNColumns(event);
            }}
            bind:value={nColumns}
          />
        </div>
      </div>
      <div class="dupo-menu-pane mb-2">
        <Label for="split_values">Split values</Label>

        <div class="dupo-menu-pane-form-row">
          <Input
            name="split_values"
            type="textarea"
            class="custom-form-select"
            style="height: 100px;"
            on:focus={inputFocusAction}
            on:blur={inputBlurAction}
            on:change={(event) => {
              updateSplitValues(event);
            }}
            bind:value={splitValues}
          />
        </div>
        <div class="dupo-menu-pane-row description">
          <p class="text-sm">
            Text values should be denoted by <code>" ... "</code>. Digit
            separators (,) should NOT be used.
          </p>
        </div>
      </div>

      <!-- lock -->
      <div class="dupo-menu-pane mb-2">
        <ItemResponse
          type="lock"
          tooltipMessage="Prevent suggesting responsive alternatives that changes the layout of the multiples."
          title="Lock multiples layout"
          token={"multiples-lock"}
          value={multiples_lock}
          {inputFocusAction}
          {inputBlurAction}
          changeAction={(event) => {
            updateLock(event, "multiples");
          }}
        />
      </div>
      <!-- expression, font size, alignment, expression, format, etc. -->
      <SplitHeaderEditor
        {inputFocusAction}
        {inputBlurAction}
        label={splitLabels}
        labelValues={splitValues}
        {updateSplitHeader}
        {updateLock}
      />
    {/if}
    <!-- Field List -->
    {#if $dataInfo.columns}
      <DataColumns {shelfDragStart} x="290px" y="80px" />
    {/if}

    {#if $directContext}
      <div class="pane-divider" />
      <div class="dupo-menu-pane mb-5">
        <div class="dupo-menu-pane-button-row">
          <button
            type="button"
            on:click={() => {
              currMenu.set("selection");
            }}><i class="bi bi-arrow-left me-1" /> Go back</button
          >
        </div>
      </div>
    {/if}
  {/if}

  <div id="detached-tracker">
    <i class="bi bi-trash" />
  </div>
</section>

{#if showFieldPanel}
  <section class="third-level-menu">
    <RCFieldEditor
      role={currFieldRole?.join("/") || ""}
      field={currField}
      closer={finishFieldEdit}
      {updateRCField}
      {currEncType}
      {currEncDataType}
      {currEncSort}
      {currBin}
      {currBinCount}
      {currAggregate}
      {currEncScaleType}
      {currEncFieldType}
      {inputFocusAction}
      {inputBlurAction}
    />
  </section>
{/if}

<style>
  .shelf .field-badge {
    margin-bottom: 0;
  }
  #detached-tracker {
    display: none;
    position: fixed;
    padding: 3px;
    background: #c20010;
    font-size: 16px;
    color: white;
    z-index: 999;
    text-align: center;
    line-height: 16px;
  }
</style>
